/**
 * Firebase Authentication Error Mapping Service
 * Maps technical Firebase error codes to user-friendly messages
 */

export interface FirebaseError {
   code: string;
   message: string;
}

export class FirebaseAuthErrorMapper {
   private static readonly errorMap: Record<string, string> = {
      // Authentication errors
      'auth/user-not-found':
         'Email not found. Please check your email address or sign up for an account.',
      'auth/wrong-password': 'Incorrect password. Please try again.',
      'auth/invalid-email': 'Invalid email address format.',
      'auth/user-disabled': 'This account has been disabled. Please contact support.',
      'auth/email-already-in-use': 'An account with this email already exists.',
      'auth/weak-password': 'Password is too weak. Please choose a stronger password.',
      'auth/invalid-credential': 'Invalid login credentials. Please check your email and password.',
      'auth/operation-not-allowed': 'This sign-in method is not enabled. Please contact support.',
      'auth/account-exists-with-different-credential':
         'An account already exists with this email using a different sign-in method.',

      // Password reset errors
      'auth/expired-action-code': 'This password reset link has expired. Please request a new one.',
      'auth/invalid-action-code': 'This password reset link is invalid. Please request a new one.',
      'auth/user-mismatch': 'This password reset link is for a different user.',

      // Email verification errors
      'auth/invalid-verification-code': 'Invalid verification code. Please try again.',
      'auth/expired-verification-code': 'Verification code has expired. Please request a new one.',

      // Network and general errors
      'auth/network-request-failed':
         'Network error. Please check your internet connection and try again.',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later.',
      'auth/requires-recent-login': 'Please sign in again to perform this action.',
      'auth/cancelled-popup-request': 'Sign-in was cancelled.',
      'auth/popup-blocked':
         'Sign-in popup was blocked by your browser. Please allow popups and try again.',
      'auth/popup-closed-by-user': 'Sign-in popup was closed before completing.',

      // Custom CouchDB auth errors (for our app)
      'couchdb/auth-failed':
         'Database authentication failed. Please contact support to reset your credentials.',
   };

   /**
    * Maps a Firebase error to a user-friendly message
    * @param error - Firebase error object or error code string
    * @returns User-friendly error message
    */
   static mapError(error: FirebaseError | string | Error): string {
      let errorCode: string;

      if (typeof error === 'string') {
         errorCode = error;
      } else if (error instanceof Error && 'code' in error) {
         errorCode = (error as FirebaseError).code;
      } else if (error instanceof Error) {
         // Handle generic errors
         return 'An unexpected error occurred. Please try again.';
      } else {
         errorCode = (error as FirebaseError).code;
      }

      // Return mapped message or a generic fallback
      return this.errorMap[errorCode] || 'An error occurred. Please try again.';
   }

   /**
    * Gets all available error mappings for reference
    * @returns Object containing all error mappings
    */
   static getErrorMappings(): Record<string, string> {
      return { ...this.errorMap };
   }

   /**
    * Checks if an error code is a Firebase auth error
    * @param errorCode - Error code to check
    * @returns True if it's a Firebase auth error
    */
   static isFirebaseAuthError(errorCode: string): boolean {
      return errorCode.startsWith('auth/') || errorCode.startsWith('couchdb/');
   }
}

export default FirebaseAuthErrorMapper;
